// # Protected Route Component for Admins
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import Loading from '../../components/common/Loading';

// # This component ensures that only admins can access admin routes
// # Admins have full access to all subscribed content as configured in session.js
const ProtectedRouteAdmin = ({ children }) => {
  const { isAuthenticated, isAdmin, isTutor, loading } = useAuthStore();
  const location = useLocation();

  // # Show loading state
  if (loading) {
    return <Loading message="Checking admin access..." />;
  }

  // # Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  // # Check admin access
  if (!isAdmin) {
    // # Handle mentor route redirections
    if (location.pathname.startsWith('/mentor/')) {
      // # Allow tutors to access their own mentee routes
      if (isTutor && (location.pathname.includes('/step1/') || location.pathname.includes('/step2/'))) {
        const path = location.pathname.replace('/mentor/', '/tutor/mentorship/');
        return <Navigate to={path} replace />;
      }
      
      // # Redirect to dashboard for non-admins/non-tutors
      return <Navigate to="/dashboard" replace />;
    }
    
    // # Handle mentorship route redirections
    if (location.pathname.startsWith('/admin/mentorship/')) {
      // # Extract the step and remaining path
      const path = location.pathname.replace('/admin/mentorship/', '');
      
      // # Only redirect step1 and step2 mentee routes
      if (path.startsWith('step1/') || path.startsWith('step2/')) {
        const [step, ...rest] = path.split('/');
        
        // # Allow tutors to access their own mentee routes
        if (isTutor) {
          return <Navigate to={`/tutor/mentorship/${step}/${rest.join('/')}`} replace />;
        }
        
        // # For non-tutors, redirect to regular mentorship route
        return <Navigate to={`/mentorship/${step}/${rest.join('/')}`} replace />;
      }
      
      // # For mentor dashboard routes, redirect to dashboard
      return <Navigate to="/dashboard" replace />;
    }
    
    // # Handle library route redirections
    if (location.pathname.startsWith('/admin/library/')) {
      // # Extract the step and remaining path
      const path = location.pathname.replace('/admin/library/', '');
      const [step, ...rest] = path.split('/');
      
      // # Handle different library routes
      if (step === 'step1' || step === 'step2') {
        // # Redirect to corresponding user library route
        return <Navigate to={`/library/${step}/${rest.join('/')}`} replace />;
      } else {
        // # For other library routes, redirect to main library
        return <Navigate to="/library" replace />;
      }
    }
    
    // # Handle leaderboards route redirection
    if (location.pathname.startsWith('/admin/leaderboards')) {
      return <Navigate to="/dashboard" replace />;
    }
    
    // # For non-mentorship/library admin routes, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  // # All checks passed, render the protected content
  return children;
};

export default ProtectedRouteAdmin;
